import React from "react";
import { useLang } from "@/i18n/LanguageContext";
import { ExternalLink } from "lucide-react";

const Ar = ({ children }) => (
  <p dir="rtl" lang="ar" className="janazah-arabic text-right text-xl leading-[2.4] text-[#0A192F]">
    {children}
  </p>
);

const Block = ({ children }) => <div className="mt-3 rounded-2xl bg-[#F1F5F9] p-4">{children}</div>;

const Translit = ({ children }) => (
  <p className="mt-3 text-sm italic leading-relaxed text-[#3E6B9A]">{children}</p>
);

const Meaning = ({ label, children }) => (
  <p className="mt-3 text-sm leading-relaxed text-[#0A1128]/75">
    <span className="font-semibold text-[#0A192F]">{label}: </span>
    {children}
  </p>
);

const Ref = ({ children }) => (
  <p className="mt-2 text-xs text-slate-400">{children}</p>
);

const Step = ({ n, title, children, testid }) => (
  <div className="maqam-card rounded-3xl p-5" data-testid={testid}>
    <div className="mb-2 flex items-center gap-3">
      <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-[#0A192F] font-display text-sm font-bold text-[#E9CE7D]">{n}</span>
      <h3 className="font-display text-lg font-bold text-[#0A192F]">{title}</h3>
    </div>
    {children}
  </div>
);

export const JanazahGuide = () => {
  const { lang } = useLang();
  const meaningLabel = lang === "fr" ? "Sens" : "Meaning";
  const translitLabel = lang === "fr" ? "Translittération" : "Transliteration";

  return (
    <div data-testid="janazah-guide">
      {/* Intro */}
      <div className="mb-4 rounded-2xl border border-[#B9D0EC] bg-[#EAF2FC] p-4" data-testid="janazah-intro">
        <p className="text-sm leading-relaxed text-[#0A1128]/80">
          Salatul Janazah is the Islamic funeral prayer. It is performed standing, facing the qiblah, with four
          takbirs, without bowing or prostration. Follow the imam leading the prayer.
        </p>
        <p className="mt-2 text-xs text-slate-500">Reference: Sahih al-Bukhari 1333 (four takbirs).</p>
      </div>

      <div className="space-y-4">
        {/* Step 1 */}
        <Step n="1" title="First Takbir — Al-Fatihah" testid="janazah-step-1">
          <p className="text-sm text-[#0A1128]/75">Say <span className="font-semibold">Allahu Akbar</span>, then recite Surah Al-Fatihah.</p>
          <Block>
            <Ar>
              بِسْمِ اللَّهِ الرَّحْمَٰنِ الرَّحِيمِ<br />
              الْحَمْدُ لِلَّهِ رَبِّ الْعَالَمِينَ<br />
              الرَّحْمَٰنِ الرَّحِيمِ<br />
              مَالِكِ يَوْمِ الدِّينِ<br />
              إِيَّاكَ نَعْبُدُ وَإِيَّاكَ نَسْتَعِينُ<br />
              اهْدِنَا الصِّرَاطَ الْمُسْتَقِيمَ<br />
              صِرَاطَ الَّذِينَ أَنْعَمْتَ عَلَيْهِمْ غَيْرِ الْمَغْضُوبِ عَلَيْهِمْ وَلَا الضَّالِّينَ
            </Ar>
          </Block>
          <Translit>
            Bismillahir-Rahmanir-Rahim. Alhamdu lillahi Rabbil-'alamin. Ar-Rahmanir-Rahim. Maliki yawmid-din.
            Iyyaka na'budu wa iyyaka nasta'in. Ihdinas-siratal-mustaqim. Siratal-ladhina an'amta 'alayhim,
            ghayril-maghdubi 'alayhim wa lad-dallin.
          </Translit>
          <Meaning label={meaningLabel}>
            In the name of Allah, the Most Merciful, the Especially Merciful. All praise belongs to Allah, Lord of
            all worlds, the Most Merciful, the Especially Merciful, Master of the Day of Judgment. You alone we
            worship, and You alone we ask for help. Guide us to the straight path, the path of those You have
            blessed, not those who have incurred anger or those who have gone astray.
          </Meaning>
          <Ref>Hadith reference: Sahih al-Bukhari 1335 (recitation of Al-Fatihah in the funeral prayer).</Ref>
        </Step>

        {/* Step 2 */}
        <Step n="2" title="Second Takbir — Blessings upon the Prophet ﷺ" testid="janazah-step-2">
          <p className="text-sm text-[#0A1128]/75">
            Say <span className="font-semibold">Allahu Akbar</span> and send blessings upon the Prophet ﷺ. The
            familiar Durood Ibrahim is presented below as a practical recitation (not attributed to a specific
            funeral-prayer narration).
          </p>
          <Block>
            <Ar>
              اللَّهُمَّ صَلِّ عَلَى مُحَمَّدٍ وَعَلَى آلِ مُحَمَّدٍ كَمَا صَلَّيْتَ عَلَى إِبْرَاهِيمَ وَعَلَى آلِ إِبْرَاهِيمَ إِنَّكَ حَمِيدٌ مَجِيدٌ<br /><br />
              اللَّهُمَّ بَارِكْ عَلَى مُحَمَّدٍ وَعَلَى آلِ مُحَمَّدٍ كَمَا بَارَكْتَ عَلَى إِبْرَاهِيمَ وَعَلَى آلِ إِبْرَاهِيمَ إِنَّكَ حَمِيدٌ مَجِيدٌ
            </Ar>
          </Block>
          <Translit>
            Allahumma salli 'ala Muhammadin wa 'ala ali Muhammad, kama sallayta 'ala Ibrahima wa 'ala ali Ibrahim,
            innaka Hamidum Majid. Allahumma barik 'ala Muhammadin wa 'ala ali Muhammad, kama barakta 'ala Ibrahima
            wa 'ala ali Ibrahim, innaka Hamidum Majid.
          </Translit>
          <Meaning label={meaningLabel}>
            O Allah, send prayers upon Muhammad and the family of Muhammad, as You sent prayers upon Ibrahim and
            the family of Ibrahim. Indeed, You are Praiseworthy and Glorious. O Allah, bless Muhammad and the
            family of Muhammad, as You blessed Ibrahim and the family of Ibrahim. Indeed, You are Praiseworthy and
            Glorious.
          </Meaning>
        </Step>

        {/* Step 3 */}
        <Step n="3" title="Third Takbir — Supplication for the Deceased" testid="janazah-step-3">
          <p className="text-sm text-[#0A1128]/75">Say <span className="font-semibold">Allahu Akbar</span> and supplicate for the deceased (for a man):</p>
          <Block>
            <Ar>
              اللَّهُمَّ اغْفِرْ لَهُ وَارْحَمْهُ وَعَافِهِ وَاعْفُ عَنْهُ وَأَكْرِمْ نُزُلَهُ وَوَسِّعْ مُدْخَلَهُ وَاغْسِلْهُ بِالْمَاءِ وَالثَّلْجِ وَالْبَرَدِ وَنَقِّهِ مِنَ الْخَطَايَا كَمَا نَقَّيْتَ الثَّوْبَ الْأَبْيَضَ مِنَ الدَّنَسِ وَأَبْدِلْهُ دَارًا خَيْرًا مِنْ دَارِهِ وَأَهْلًا خَيْرًا مِنْ أَهْلِهِ وَزَوْجًا خَيْرًا مِنْ زَوْجِهِ وَأَدْخِلْهُ الْجَنَّةَ وَأَعِذْهُ مِنْ عَذَابِ الْقَبْرِ وَمِنْ عَذَابِ النَّارِ
            </Ar>
          </Block>
          <Translit>
            Allahummaghfir lahu warhamhu, wa 'afihi wa'fu 'anhu, wa akrim nuzulahu, wa wassi' mudkhalahu, waghsilhu
            bil-ma'i wath-thalji wal-barad, wa naqqihi minal-khataya kama naqqaytath-thawbal-abyada minad-danas, wa
            abdilhu daran khayran min darihi, wa ahlan khayran min ahlihi, wa zawjan khayran min zawjihi, wa
            adkhilhul-jannata wa a'idhhu min 'adhabil-qabri wa min 'adhabin-nar.
          </Translit>
          <Meaning label={meaningLabel}>
            O Allah, forgive him, have mercy on him, grant him well-being and pardon him. Honour his reception and
            make his entrance spacious. Wash him with water, snow and hail. Purify him of sins as a white garment
            is cleansed of stains. Replace his home with a better home, his family with a better family and his
            spouse with a better spouse. Admit him into Paradise and protect him from the punishment of the grave
            and the Fire.
          </Meaning>
          <Ref>Reported in Sahih Muslim 963a–b.</Ref>

          <div className="mt-4 rounded-2xl border border-slate-200 p-4">
            <p className="mb-2 text-xs font-bold uppercase tracking-wide text-[#3E6B9A]">Shorter option</p>
            <p className="text-xs text-slate-500">These are abbreviated adaptations of the supplication above, not separate verbatim narrations.</p>
            <div className="mt-3">
              <p className="text-sm font-semibold text-[#0A192F]">For a man:</p>
              <Ar>اللَّهُمَّ اغْفِرْ لَهُ وَارْحَمْهُ</Ar>
              <Translit>Allahummaghfir lahu warhamhu.</Translit>
              <Meaning label={meaningLabel}>O Allah, forgive him and have mercy on him.</Meaning>
            </div>
            <div className="mt-3 border-t border-slate-100 pt-3">
              <p className="text-sm font-semibold text-[#0A192F]">For a woman:</p>
              <Ar>اللَّهُمَّ اغْفِرْ لَهَا وَارْحَمْهَا</Ar>
              <Translit>Allahummaghfir laha warhamha.</Translit>
              <Meaning label={meaningLabel}>O Allah, forgive her and have mercy on her.</Meaning>
            </div>
          </div>
          <Ref>
            For a deceased child, the child is included in the funeral supplication — follow the imam. (No separate
            child-specific wording is attributed to Sahih al-Bukhari or Sahih Muslim here.)
          </Ref>
        </Step>

        {/* Step 4 */}
        <Step n="4" title="Fourth Takbir — Conclusion" testid="janazah-step-4">
          <p className="text-sm text-[#0A1128]/75">Say <span className="font-semibold">Allahu Akbar</span>, pause briefly, then conclude with salam, following the imam.</p>
          <Block>
            <Ar>السَّلَامُ عَلَيْكُمْ وَرَحْمَةُ اللَّهِ</Ar>
          </Block>
          <Translit>Assalamu 'alaykum wa rahmatullah.</Translit>
          <Meaning label={meaningLabel}>Peace and Allah's mercy be upon you.</Meaning>
          <Ref>Presented as practical concluding guidance; the cited four-takbir narration does not establish the exact salam wording.</Ref>
        </Step>

        {/* Sources */}
        <div className="maqam-card rounded-3xl p-5" data-testid="janazah-sources">
          <h3 className="mb-3 font-display text-base font-bold text-[#0A192F]">Sources</h3>
          <div className="space-y-2">
            {[
              ["Sahih al-Bukhari 1333", "https://sunnah.com/bukhari:1333"],
              ["Sahih al-Bukhari 1335", "https://sunnah.com/bukhari:1335"],
              ["Sahih Muslim 963a", "https://sunnah.com/muslim:963a"],
              ["Sahih Muslim 963b", "https://sunnah.com/muslim:963b"],
            ].map(([label, url]) => (
              <a key={url} href={url} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-sm font-medium text-[#3E6B9A] hover:underline">
                <ExternalLink size={14} /> {label}
              </a>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
